import type { EvalSample, TrainedModel } from '../data/types';

/**
 * Per-eval-sample result after running it through the trained model.
 * `predictedClassId` is null if the prediction missed every label
 * (shouldn't happen with a healthy model, but kept defensive).
 */
export interface EvalResult {
  sample: EvalSample;
  predictedClassId: string | null;
  predictedClassName: string | null;
  /** True iff the predicted class matches the truth class. */
  correct: boolean;
  /** True iff this sample was captured strictly after the model was trained. */
  heldOut: boolean;
  topConfidence: number;
}

export interface EvalScore {
  /** All results, oldest first. */
  results: EvalResult[];
  /** Number of held-out samples (captured after `trainedAt`). */
  heldOutTotal: number;
  /** Number of held-out samples the model got right. */
  heldOutCorrect: number;
  /** Held-out accuracy in [0, 1]. NaN when heldOutTotal is 0. */
  heldOutAccuracy: number;
  /** Per-class breakdown, only counting held-out samples. */
  perClass: Record<string, { total: number; correct: number; accuracy: number }>;
  /** Number of stale samples (captured before the most recent training). */
  staleTotal: number;
}

/**
 * Compute the held-out evaluation score for a project.
 *
 * The Phase 1 gate says: "Reported accuracy is computed on samples captured
 * in a separate session from training, not via random split." We approximate
 * "separate session" with `sample.createdAt > trainedModel.trainedAt`, which
 * is the strictest mechanical guarantee available without timestamp
 * tampering — and that's the contract the gate test asserts.
 */
export function scoreEval(
  trained: TrainedModel | null,
  evalSamples: EvalSample[],
  predictionsBySampleId: Record<string, number[] | undefined>,
): EvalScore {
  const trainedAt = trained?.trainedAt ?? Number.POSITIVE_INFINITY;
  const labelById = new Map(trained?.labels.map((l) => [l.classId, l.name]) ?? []);

  const results: EvalResult[] = evalSamples.map((sample) => {
    const probs = predictionsBySampleId[sample.id];
    let predictedClassId: string | null = null;
    let topConfidence = 0;
    if (probs && probs.length > 0 && trained) {
      let bestIndex = 0;
      let best = probs[0] ?? 0;
      for (let i = 1; i < probs.length; i++) {
        const value = probs[i] ?? 0;
        if (value > best) {
          best = value;
          bestIndex = i;
        }
      }
      predictedClassId = trained.labels[bestIndex]?.classId ?? null;
      topConfidence = best;
    }
    return {
      sample,
      predictedClassId,
      predictedClassName: predictedClassId ? (labelById.get(predictedClassId) ?? null) : null,
      correct: predictedClassId !== null && predictedClassId === sample.classId,
      heldOut: trained ? sample.createdAt > trainedAt : false,
      topConfidence,
    };
  });

  const heldOut = results.filter((r) => r.heldOut);
  const heldOutCorrect = heldOut.filter((r) => r.correct).length;
  const heldOutAccuracy = heldOut.length === 0 ? Number.NaN : heldOutCorrect / heldOut.length;

  const perClass: EvalScore['perClass'] = {};
  for (const result of heldOut) {
    const key = result.sample.classId;
    const bucket = perClass[key] ?? { total: 0, correct: 0, accuracy: 0 };
    bucket.total++;
    if (result.correct) bucket.correct++;
    bucket.accuracy = bucket.correct / bucket.total;
    perClass[key] = bucket;
  }

  return {
    results,
    heldOutTotal: heldOut.length,
    heldOutCorrect,
    heldOutAccuracy,
    perClass,
    staleTotal: results.length - heldOut.length,
  };
}
